import { useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
  Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress,
} from '@mui/material'
import { Add, MoreVert, Done, Block } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listMaintenanceSchedules, markMaintenanceServiceDone, deactivateMaintenanceSchedule, type MaintenanceSchedule } from '../../api/mentenanta'
import { errorMessage } from '../../api/client'
import MaintenanceScheduleFormDialog from './MaintenanceScheduleFormDialog'

export default function MaintenanceCalendarPage() {
  const [formOpen, setFormOpen] = useState(false)
  const [markDoneTarget, setMarkDoneTarget] = useState<MaintenanceSchedule | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: MaintenanceSchedule } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['mentenanta', 'calendar'], queryFn: listMaintenanceSchedules })
  const closeMenu = () => setMenuAnchor(null)
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['mentenanta', 'calendar'] })

  const deactivateMutation = useMutation({ mutationFn: deactivateMaintenanceSchedule, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })

  const columns = useMemo<GridColDef<MaintenanceSchedule>[]>(() => [
    { field: 'equipmentName', headerName: 'Echipament', flex: 1, minWidth: 200, renderCell: (p) => `${p.row.equipmentCode} — ${p.row.equipmentName}` },
    { field: 'interventionType', headerName: 'Intervenție', width: 180 },
    { field: 'intervalType', headerName: 'Interval', width: 120 },
    { field: 'lastServiceDate', headerName: 'Ultima', width: 110 },
    { field: 'nextDueDate', headerName: 'Scadență', width: 110 },
    {
      field: 'stare', headerName: 'Stare', width: 150, sortable: false,
      renderCell: (p) => p.row.escalated
        ? <Chip size="small" label="Escaladat" color="error" variant="outlined" />
        : p.row.overdue
          ? <Chip size="small" label="Restant" color="warning" variant="outlined" />
          : p.row.dueSoon
            ? <Chip size="small" label="Apropiat" color="info" variant="outlined" />
            : <Chip size="small" label="OK" color="success" variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Mentenanță</Typography>
        <Typography variant="inherit" color="text.primary">Calendar mentenanță</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Calendar mentenanță</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormOpen(true)}>Calendar nou</Button>
      </Box>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>

      <MaintenanceScheduleFormDialog open={formOpen} onClose={() => setFormOpen(false)} />
      <MarkDoneDialog target={markDoneTarget} onClose={() => setMarkDoneTarget(null)} onDone={invalidate} />

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="markDone" onClick={() => { setMarkDoneTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Done fontSize="small" /></ListItemIcon><ListItemText>Marchează efectuată</ListItemText>
          </MenuItem>,
          <MenuItem key="deactivate" disabled={!menuAnchor.row.isActive}
            onClick={() => { deactivateMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><Block fontSize="small" /></ListItemIcon><ListItemText>Dezactivează</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}

function MarkDoneDialog({ target, onClose, onDone }: { target: MaintenanceSchedule | null; onClose: () => void; onDone: () => void }) {
  const open = target !== null
  const [serviceDate, setServiceDate] = useState(new Date().toISOString().slice(0, 10))
  const mutation = useMutation({
    mutationFn: () => markMaintenanceServiceDone(target!.id, serviceDate),
    onSuccess: () => { onDone(); onClose() },
  })
  const handleClose = () => { mutation.reset(); onClose() }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Marchează efectuată — {target?.interventionType}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField type="date" label="Data intervenției" value={serviceDate} onChange={(e) => setServiceDate(e.target.value)}
            fullWidth slotProps={{ inputLabel: { shrink: true } }} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose}>Anulează</Button>
        <Button variant="contained" onClick={() => mutation.mutate()} disabled={mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
          Confirmă
        </Button>
      </DialogActions>
    </Dialog>
  )
}
